import React from 'react';
import Form from 'react-bootstrap/Form';
import { BadgeWithTooltip } from '../../../../ui/AvatarTool/BadgeWithTooltip/BadgeWithTooltip';

/** The app-default mulberry hue used when an operator enables a custom ring colour. */
export const DEFAULT_RING_COLOR = '#7B2C9E';

const RING_COLOUR_INFO = {
  title: 'Ring Colour',
  content: 'Colours the ring around the card and the countdown timer. If left empty, the app uses its default violet.',
} as const;

export interface RingColorPickerProps {
  /** Empty means no custom colour; a hex value enables the native picker. */
  value: string;
  /** Called for every explicit operator action, including enable and clear. */
  onChange: (value: string) => void;
  /** Validation is owned by the containing form, so this component stays form-library agnostic. */
  error?: string;
}

/**
 * Event ring-colour controls shared by the Events form and the Promotions reward banner.
 *
 * The parent owns both form state and wire-intent state. In particular, its `onChange`
 * callback can mark its ring colour as touched so an untouched value is omitted from a
 * save payload while a deliberately cleared value is sent as an empty string.
 */
export function RingColorPicker({ value, onChange, error }: RingColorPickerProps) {
  const ringColorEnabled = !!value;

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked ? DEFAULT_RING_COLOR : '');
  };

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  return (
    <Form.Group>
      <Form.Label style={{ position: 'relative', display: 'inline-block' }}>
        Ring Colour
        <div className="position-absolute" style={{ top: '-1em', right: '-1.5em' }}>
          <BadgeWithTooltip
            id="info-ring-colour"
            title={RING_COLOUR_INFO.title}
            content={RING_COLOUR_INFO.content}
            placement="right"
          />
        </div>
      </Form.Label>
      <div className="d-flex align-items-center" style={{ gap: '12px', flexWrap: 'wrap' }}>
        <Form.Check
          type="checkbox"
          id="ringColorEnabled"
          label="Use custom colour"
          checked={ringColorEnabled}
          onChange={handleToggle}
        />
        {ringColorEnabled && (
          <>
            <input
              type="color"
              aria-label="Ring colour picker"
              value={value || DEFAULT_RING_COLOR}
              onChange={handleColorChange}
              style={{
                width: 44,
                height: 36,
                padding: 2,
                cursor: 'pointer',
                border: '1px solid #ced4da',
                borderRadius: 4,
              }}
            />
            {/* Preview ring matching EventOval in dorian-lib-creation-tools-ui EventShelfCard.tsx. */}
            <div
              aria-label="Ring colour preview"
              style={{
                width: 32,
                height: 36,
                borderRadius: '50%',
                border: `4px solid ${value || DEFAULT_RING_COLOR}`,
                backgroundColor: 'transparent',
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
              {value || DEFAULT_RING_COLOR}
            </span>
          </>
        )}
      </div>
      {error && (
        <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: 4 }}>
          {error}
        </div>
      )}
    </Form.Group>
  );
}
